import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import {
  getProgramWbsItemCriticalPaths,
  saveProgramWbsItemCriticalPaths,
  ProgramWbsItemCriticalPathResponse,
} from '../../../../infrastructure/api/wbs-item-critical-paths'
import { successDummyResponse, APIResponse } from '../../../../lib/commons/api'
import { CriticalPathLinkData } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/GanttCriticalPath/CriticalPathContext'
import { WbsItemInfo } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/types'
import {
  WbsItemLinkSummary,
  WBS_ITEM_LINK_SOURCE,
} from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/WbsItemLink/WbsItemLinkDialog'
import { CalendarDateVO } from '../../../../domain/value-object/CalendarDateVO'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { dateTimeV2Service } from '../../../../domain/value-object/DateTimeV2'
import {
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'

const getNameFromGrid = (
  api: GridApi<ProgramManagementRow>,
  rowIndex: number
): string => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return ''
  return rowNode.data?.displayName ?? ''
}

const getIconUrlFromGrid = (
  api: GridApi<ProgramManagementRow>,
  rowIndex: number
): string | undefined => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return undefined
  return rowNode.data?.iconUrl
}

const getScheduledDateFromGrid = (
  api: GridApi<ProgramManagementRow>,
  rowIndex: number
): { startDate?: string; endDate?: string } | undefined => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return undefined
  return rowNode.data?.scheduledDate
}

export type UseProgramCriticalPathDataReturn = {
  criticalPaths: CriticalPathLinkData[]
  wbsItemInfoMap: ReadonlyMap<string, WbsItemInfo>
  dialogItems: WbsItemLinkSummary[]
  onCriticalPathsChange: (links: CriticalPathLinkData[]) => void
  saveProgramCriticalPaths: () => Promise<APIResponse>
  updateLinkFromDialog: (
    uuid: string,
    updates: {
      color?: string
      description?: string
      fromDate?: string
      toDate?: string
    }
  ) => void
  removeLinkFromDialog: (uuid: string) => void
  removeByWbsItemUuids: (wbsItemUuids: ReadonlySet<string>) => void
  fetchProgramCriticalPaths: () => void
  hasUnsavedChanges: () => boolean
}

/**
 * プログラム管理画面用のプログラムCP取得・保存hook。
 * useCrossProjectCriticalPathData をベースに、editable: true の編集可能CPを管理する。
 */
export const useProgramCriticalPathData = (
  programUuid: string | undefined,
  rows: ProgramManagementRow[],
  gridApi: GridApi<ProgramManagementRow> | null,
  crossProjectCpUuids?: ReadonlySet<string>
): UseProgramCriticalPathDataReturn => {
  const [criticalPaths, setCriticalPaths] = useState<CriticalPathLinkData[]>([])
  const [dialogItems, setDialogItems] = useState<WbsItemLinkSummary[]>([])
  const serverDataRef = useRef<ProgramWbsItemCriticalPathResponse[]>([])
  const currentLinksRef = useRef<CriticalPathLinkData[]>([])
  const dialogItemsRef = useRef<WbsItemLinkSummary[]>([])
  const fetchedForRef = useRef<string | null>(null)

  const fetchCriticalPaths = useCallback(async () => {
    if (!gridApi || !programUuid || rows.length === 0) return

    // 同じプログラムに対して二重フェッチを防止
    if (fetchedForRef.current === programUuid) return
    fetchedForRef.current = programUuid

    try {
      const response = await getProgramWbsItemCriticalPaths(programUuid)
      if (response.hasError) return

      const serverData =
        (response.json as ProgramWbsItemCriticalPathResponse[]) ?? []
      serverDataRef.current = serverData

      // AG Grid の表示行から projectPlanUuid → rowIndex マッピングを構築
      const projectPlanUuidToRowIndex = new Map<string, number>()
      gridApi.forEachNodeAfterFilterAndSort(node => {
        if (node.data?.uuid && node.rowIndex != null && node.rowIndex >= 0) {
          projectPlanUuidToRowIndex.set(node.data.uuid, node.rowIndex)
        }
      })

      // wbsItemInfoMap 構築（非表示行の displayName/icon 解決用）
      const infoMap = new Map<string, WbsItemInfo>()
      for (const s of serverData) {
        if (s.fromWbsItem) {
          infoMap.set(s.fromProjectPlanUuid, {
            displayName: s.fromWbsItem.displayName ?? '',
            iconUrl: s.fromWbsItem.typeDto?.iconUrl,
          })
        }
        if (s.toWbsItem) {
          infoMap.set(s.toProjectPlanUuid, {
            displayName: s.toWbsItem.displayName ?? '',
            iconUrl: s.toWbsItem.typeDto?.iconUrl,
          })
        }
      }

      // 端点（行uuid = projectPlanUuid）から祖先 PROJECT 行を辿り、プロジェクト名/アイコンを解決する。
      // バックエンドが fromProject/toProject を返さない場合のフォールバックに使う。
      const rowByUuid = new Map(rows.map(r => [r.uuid, r]))
      const resolveProject = (
        projectPlanUuid: string
      ): { name: string; iconUrl?: string } | undefined => {
        const row = rowByUuid.get(projectPlanUuid)
        if (!row) return undefined
        for (let i = row.treeValue.length - 1; i >= 0; i--) {
          const ancestor = rowByUuid.get(row.treeValue[i])
          if (
            ancestor?.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT &&
            ancestor.projectUuid
          ) {
            return {
              name: ancestor.displayName ?? '',
              iconUrl: ancestor.iconUrl,
            }
          }
        }
        return undefined
      }

      // CriticalPathLinkData への変換（editable: true）
      const links: CriticalPathLinkData[] = serverData.map(s => {
        const createdAtVo = dateTimeV2Service.fromApiResponseToVo(s.createdAt)
        const updatedAtVo = dateTimeV2Service.fromApiResponseToVo(s.updatedAt)
        return {
          uuid: s.uuid,
          fromRowIndex:
            projectPlanUuidToRowIndex.get(s.fromProjectPlanUuid) ?? -1,
          toRowIndex: projectPlanUuidToRowIndex.get(s.toProjectPlanUuid) ?? -1,
          fromDate: CalendarDateVO.of(s.fromDate),
          toDate: CalendarDateVO.of(s.toDate),
          fromWbsItemUuid: s.fromWbsItemUuid,
          toWbsItemUuid: s.toWbsItemUuid,
          fromProjectPlanUuid: s.fromProjectPlanUuid,
          toProjectPlanUuid: s.toProjectPlanUuid,
          editable: true,
          ...(s.color != null && { color: s.color }),
          ...(s.description != null && { description: s.description }),
          createdAt: createdAtVo
            ? dateTimeV2Service.format(createdAtVo)
            : undefined,
          createdBy: s.createdBy,
          updatedAt: updatedAtVo
            ? dateTimeV2Service.format(updatedAtVo)
            : undefined,
          updatedBy: s.updatedBy,
        }
      })

      setCriticalPaths(links)
      currentLinksRef.current = links

      // dialog items の構築
      const allDialogItems: WbsItemLinkSummary[] = serverData.map(s => {
        const fromRowIndex = projectPlanUuidToRowIndex.get(
          s.fromProjectPlanUuid
        )
        const toRowIndex = projectPlanUuidToRowIndex.get(s.toProjectPlanUuid)
        const createdAtVo = dateTimeV2Service.fromApiResponseToVo(s.createdAt)
        const updatedAtVo = dateTimeV2Service.fromApiResponseToVo(s.updatedAt)
        return {
          uuid: s.uuid,
          fromWbsItemUuid: s.fromWbsItemUuid,
          toWbsItemUuid: s.toWbsItemUuid,
          fromProjectPlanUuid: s.fromProjectPlanUuid,
          toProjectPlanUuid: s.toProjectPlanUuid,
          fromName: infoMap.get(s.fromProjectPlanUuid)?.displayName ?? '',
          toName: infoMap.get(s.toProjectPlanUuid)?.displayName ?? '',
          fromIconUrl: infoMap.get(s.fromProjectPlanUuid)?.iconUrl,
          toIconUrl: infoMap.get(s.toProjectPlanUuid)?.iconUrl,
          // プロジェクト名はレスポンスの fromProject/toProject を優先し、
          // 無ければグリッドの祖先 PROJECT 行から解決する
          fromProjectName:
            s.fromProject?.name ?? resolveProject(s.fromProjectPlanUuid)?.name,
          fromProjectIconUrl:
            s.fromProject?.iconUrl ??
            resolveProject(s.fromProjectPlanUuid)?.iconUrl,
          toProjectName:
            s.toProject?.name ?? resolveProject(s.toProjectPlanUuid)?.name,
          toProjectIconUrl:
            s.toProject?.iconUrl ??
            resolveProject(s.toProjectPlanUuid)?.iconUrl,
          fromDate: s.fromDate,
          toDate: s.toDate,
          fromScheduledDate:
            fromRowIndex !== undefined
              ? getScheduledDateFromGrid(gridApi, fromRowIndex)
              : undefined,
          toScheduledDate:
            toRowIndex !== undefined
              ? getScheduledDateFromGrid(gridApi, toRowIndex)
              : undefined,
          color: s.color,
          description: s.description,
          createdAt: createdAtVo
            ? dateTimeV2Service.format(createdAtVo)
            : undefined,
          createdBy: s.createdBy,
          updatedAt: updatedAtVo
            ? dateTimeV2Service.format(updatedAtVo)
            : undefined,
          updatedBy: s.updatedBy,
          source: WBS_ITEM_LINK_SOURCE.PROGRAM,
          editable: true,
        }
      })
      dialogItemsRef.current = allDialogItems
      setDialogItems(allDialogItems)
    } catch {
      // API エラーはサイレントに扱う
    }
  }, [programUuid, rows, gridApi])

  useEffect(() => {
    fetchCriticalPaths()
  }, [fetchCriticalPaths])

  // programUuid が変わったらフェッチ済みキーをリセット
  useEffect(() => {
    fetchedForRef.current = null
  }, [programUuid])

  const onCriticalPathsChange = useCallback(
    (links: CriticalPathLinkData[]) => {
      // editable: true のリンクのみをこのhookが管理する
      const editableLinks = links.filter(l => l.editable !== false)
      const prevLinks = currentLinksRef.current
      currentLinksRef.current = editableLinks

      // Sync changes to dialogItems
      setDialogItems(prev => {
        let updated = prev

        // 削除されたアイテムを反映
        const removedUuids = new Set(
          prevLinks
            .filter(p => !editableLinks.some(l => l.uuid === p.uuid))
            .map(p => p.uuid)
        )
        if (removedUuids.size > 0) {
          updated = updated.filter(item => !removedUuids.has(item.uuid))
        }

        // 追加されたアイテムを反映
        const newItems = editableLinks.filter(
          l => !prev.some(p => p.uuid === l.uuid)
        )
        if (newItems.length > 0) {
          const newDialogItems: WbsItemLinkSummary[] = newItems.map(l => ({
            uuid: l.uuid,
            fromWbsItemUuid: l.fromWbsItemUuid,
            toWbsItemUuid: l.toWbsItemUuid,
            fromProjectPlanUuid: l.fromProjectPlanUuid,
            toProjectPlanUuid: l.toProjectPlanUuid,
            fromName: gridApi
              ? getNameFromGrid(gridApi, l.fromRowIndex)
              : l.fromWbsItemUuid,
            toName: gridApi
              ? getNameFromGrid(gridApi, l.toRowIndex)
              : l.toWbsItemUuid,
            fromIconUrl: gridApi
              ? getIconUrlFromGrid(gridApi, l.fromRowIndex)
              : undefined,
            toIconUrl: gridApi
              ? getIconUrlFromGrid(gridApi, l.toRowIndex)
              : undefined,
            fromDate: dateV2Service.fromVoToApiRequest(l.fromDate.date),
            toDate: dateV2Service.fromVoToApiRequest(l.toDate.date),
            fromScheduledDate: gridApi
              ? getScheduledDateFromGrid(gridApi, l.fromRowIndex)
              : undefined,
            toScheduledDate: gridApi
              ? getScheduledDateFromGrid(gridApi, l.toRowIndex)
              : undefined,
            color: l.color ?? null,
            description: l.description ?? null,
            source: WBS_ITEM_LINK_SOURCE.PROGRAM,
            editable: true,
          }))
          updated = [...updated, ...newDialogItems]
        }

        // color/description/date の変更を同期
        const linksMap = new Map(editableLinks.map(l => [l.uuid, l]))
        updated = updated.map(item => {
          const link = linksMap.get(item.uuid)
          if (!link) return item
          const newColor = link.color ?? null
          const newDesc = link.description ?? null
          const newFromDate = dateV2Service.fromVoToApiRequest(
            link.fromDate.date
          )
          const newToDate = dateV2Service.fromVoToApiRequest(link.toDate.date)
          if (
            newColor === item.color &&
            newDesc === item.description &&
            newFromDate === item.fromDate &&
            newToDate === item.toDate
          ) {
            return item
          }
          return {
            ...item,
            color: newColor,
            description: newDesc,
            fromDate: newFromDate,
            toDate: newToDate,
          }
        })

        dialogItemsRef.current = updated
        return updated
      })
    },
    [gridApi]
  )

  const updateLinkFromDialog = useCallback(
    (
      uuid: string,
      updates: {
        color?: string
        description?: string
        fromDate?: string
        toDate?: string
      }
    ) => {
      setDialogItems(prev => {
        const next = prev.map(item =>
          item.uuid === uuid
            ? {
                ...item,
                ...(updates.color !== undefined && {
                  color: updates.color ?? null,
                }),
                ...(updates.description !== undefined && {
                  description: updates.description ?? null,
                }),
                ...(updates.fromDate !== undefined && {
                  fromDate: updates.fromDate,
                }),
                ...(updates.toDate !== undefined && {
                  toDate: updates.toDate,
                }),
              }
            : item
        )
        dialogItemsRef.current = next
        return next
      })

      const linkUpdates: Partial<CriticalPathLinkData> = {}
      if (updates.color !== undefined) {
        linkUpdates.color = updates.color
      }
      if (updates.description !== undefined) {
        linkUpdates.description = updates.description
      }
      if (updates.fromDate !== undefined) {
        linkUpdates.fromDate = CalendarDateVO.of(updates.fromDate)
      }
      if (updates.toDate !== undefined) {
        linkUpdates.toDate = CalendarDateVO.of(updates.toDate)
      }

      currentLinksRef.current = currentLinksRef.current.map(l =>
        l.uuid === uuid ? { ...l, ...linkUpdates } : l
      )
      setCriticalPaths(prev =>
        prev.map(l => (l.uuid === uuid ? { ...l, ...linkUpdates } : l))
      )
    },
    []
  )

  const removeLinkFromDialog = useCallback((uuid: string) => {
    setDialogItems(prev => {
      const next = prev.filter(item => item.uuid !== uuid)
      dialogItemsRef.current = next
      return next
    })

    currentLinksRef.current = currentLinksRef.current.filter(
      l => l.uuid !== uuid
    )
    setCriticalPaths(prev => prev.filter(l => l.uuid !== uuid))
  }, [])

  // 工程（WbsItem）が削除された際に、その工程を端点に持つ編集可能CPを除去する。
  // 行削除時に呼び、未保存の新規CPがサーバー登録されないようにする（端点が消えるため）。
  const removeByWbsItemUuids = useCallback(
    (wbsItemUuids: ReadonlySet<string>) => {
      if (wbsItemUuids.size === 0) return
      const isAffected = (item: {
        fromWbsItemUuid: string
        toWbsItemUuid: string
      }) =>
        wbsItemUuids.has(item.fromWbsItemUuid) ||
        wbsItemUuids.has(item.toWbsItemUuid)

      currentLinksRef.current = currentLinksRef.current.filter(
        l => !isAffected(l)
      )
      setCriticalPaths(prev => prev.filter(l => !isAffected(l)))
      setDialogItems(prev => {
        const next = prev.filter(item => !isAffected(item))
        dialogItemsRef.current = next
        return next
      })
    },
    []
  )

  const saveProgramCriticalPaths =
    useCallback(async (): Promise<APIResponse> => {
      const current = currentLinksRef.current
      const allDialogCurrent = dialogItemsRef.current
      const server = serverDataRef.current

      const added = current
        .filter(l => !server.some(s => s.uuid === l.uuid))
        .map(l => ({
          uuid: l.uuid,
          programUuid: programUuid!,
          fromWbsItemUuid: l.fromWbsItemUuid,
          fromDate: dateV2Service.fromVoToApiRequest(l.fromDate.date) ?? '',
          toWbsItemUuid: l.toWbsItemUuid,
          toDate: dateV2Service.fromVoToApiRequest(l.toDate.date) ?? '',
          color: l.color,
          description: l.description,
        }))

      // 編集されたアイテム（表示中）
      const editedFromContext = current
        .filter(l => {
          const s = server.find(sv => sv.uuid === l.uuid)
          if (!s) return false
          return (
            dateV2Service.fromVoToApiRequest(l.fromDate.date) !== s.fromDate ||
            dateV2Service.fromVoToApiRequest(l.toDate.date) !== s.toDate ||
            (l.color ?? null) !== (s.color ?? null) ||
            (l.description ?? null) !== (s.description ?? null)
          )
        })
        .map(l => ({
          uuid: l.uuid,
          fromDate: dateV2Service.fromVoToApiRequest(l.fromDate.date) ?? '',
          toDate: dateV2Service.fromVoToApiRequest(l.toDate.date) ?? '',
          color: l.color,
          description: l.description,
        }))

      // 編集されたアイテム（ダイアログ経由、非表示）
      const editedFromDialog = allDialogCurrent
        .filter(d => {
          if (current.some(c => c.uuid === d.uuid)) return false
          const s = server.find(sv => sv.uuid === d.uuid)
          if (!s) return false
          return (
            (d.color ?? null) !== (s.color ?? null) ||
            (d.description ?? null) !== (s.description ?? null) ||
            (d.fromDate ?? null) !== (s.fromDate ?? null) ||
            (d.toDate ?? null) !== (s.toDate ?? null)
          )
        })
        .map(d => {
          const s = server.find(sv => sv.uuid === d.uuid)!
          return {
            uuid: d.uuid,
            fromDate: d.fromDate ?? s.fromDate,
            toDate: d.toDate ?? s.toDate,
            color: d.color ?? undefined,
            description: d.description ?? undefined,
          }
        })

      const edited = [...editedFromContext, ...editedFromDialog]

      const allCurrentUuids = new Set([
        ...current.map(l => l.uuid),
        ...allDialogCurrent.map(d => d.uuid),
      ])
      const deleted = server
        .filter(
          s => !allCurrentUuids.has(s.uuid) && !crossProjectCpUuids?.has(s.uuid)
        )
        .map(s => ({ uuid: s.uuid }))

      if (!added.length && !edited.length && !deleted.length) {
        return successDummyResponse
      }

      const response = await saveProgramWbsItemCriticalPaths({
        programUuid: programUuid!,
        added,
        edited,
        deleted,
      })
      if (!response.hasError) {
        // サーバーデータを再構築
        const newServer: ProgramWbsItemCriticalPathResponse[] = []

        for (const l of current) {
          const existing = server.find(s => s.uuid === l.uuid)
          newServer.push({
            uuid: l.uuid,
            fromProject: existing?.fromProject ?? null,
            toProject: existing?.toProject ?? null,
            fromWbsItemUuid: l.fromWbsItemUuid,
            fromProjectPlanUuid:
              l.fromProjectPlanUuid ?? existing?.fromProjectPlanUuid ?? '',
            fromDate: dateV2Service.fromVoToApiRequest(l.fromDate.date) ?? '',
            toWbsItemUuid: l.toWbsItemUuid,
            toProjectPlanUuid:
              l.toProjectPlanUuid ?? existing?.toProjectPlanUuid ?? '',
            toDate: dateV2Service.fromVoToApiRequest(l.toDate.date) ?? '',
            color: l.color ?? null,
            description: l.description ?? null,
            lockVersion: existing?.lockVersion ?? 0,
            fromWbsItem: existing?.fromWbsItem ?? ({} as never),
            toWbsItem: existing?.toWbsItem ?? ({} as never),
            createdAt: existing?.createdAt ?? 0,
            createdBy: existing?.createdBy ?? ({} as never),
            updatedAt: existing?.updatedAt ?? 0,
            updatedBy: existing?.updatedBy ?? ({} as never),
          })
        }

        for (const d of allDialogCurrent) {
          if (current.some(c => c.uuid === d.uuid)) continue
          const existing = server.find(s => s.uuid === d.uuid)
          if (!existing) continue
          newServer.push({
            ...existing,
            fromDate: d.fromDate ?? existing.fromDate,
            toDate: d.toDate ?? existing.toDate,
            color: d.color,
            description: d.description,
          })
        }

        serverDataRef.current = newServer
        // 保存後の reload() で API から再取得させる。これをしないと、ドラッグで描いた
        // リンクは Context state にしか無いため、reload に伴う prop 同期で消えてしまう
        // （保存済みデータは criticalPaths state に未反映のまま上書きされる）。
        fetchedForRef.current = null
      }
      return response
    }, [programUuid, crossProjectCpUuids])

  const wbsItemInfoMap = useMemo(() => {
    const map = new Map<string, WbsItemInfo>()
    for (const item of dialogItems) {
      map.set(item.fromWbsItemUuid, {
        displayName: item.fromName,
        iconUrl: item.fromIconUrl ?? undefined,
        projectName: item.fromProjectName,
        projectIconUrl: item.fromProjectIconUrl,
      })
      map.set(item.toWbsItemUuid, {
        displayName: item.toName,
        iconUrl: item.toIconUrl ?? undefined,
        projectName: item.toProjectName,
        projectIconUrl: item.toProjectIconUrl,
      })
    }
    return map
  }, [dialogItems])

  // 未保存のCP変更があるか（追加/削除/編集）。SavePopper 表示判定に使う。
  // saveProgramCriticalPaths の差分計算と同じロジック。
  const hasUnsavedChanges = useCallback((): boolean => {
    const current = currentLinksRef.current
    const allDialog = dialogItemsRef.current
    const server = serverDataRef.current

    // 追加
    if (current.some(l => !server.some(s => s.uuid === l.uuid))) return true

    // 削除（cross-project の CP は対象外）
    const allCurrentUuids = new Set([
      ...current.map(l => l.uuid),
      ...allDialog.map(d => d.uuid),
    ])
    if (
      server.some(
        s => !allCurrentUuids.has(s.uuid) && !crossProjectCpUuids?.has(s.uuid)
      )
    ) {
      return true
    }

    // 編集（表示中）
    if (
      current.some(l => {
        const s = server.find(sv => sv.uuid === l.uuid)
        if (!s) return false
        return (
          dateV2Service.fromVoToApiRequest(l.fromDate.date) !== s.fromDate ||
          dateV2Service.fromVoToApiRequest(l.toDate.date) !== s.toDate ||
          (l.color ?? null) !== (s.color ?? null) ||
          (l.description ?? null) !== (s.description ?? null)
        )
      })
    ) {
      return true
    }

    // 編集（ダイアログ経由・非表示）
    return allDialog.some(d => {
      if (current.some(c => c.uuid === d.uuid)) return false
      const s = server.find(sv => sv.uuid === d.uuid)
      if (!s) return false
      return (
        (d.color ?? null) !== (s.color ?? null) ||
        (d.description ?? null) !== (s.description ?? null) ||
        (d.fromDate ?? null) !== (s.fromDate ?? null) ||
        (d.toDate ?? null) !== (s.toDate ?? null)
      )
    })
  }, [crossProjectCpUuids])

  return {
    criticalPaths,
    wbsItemInfoMap,
    dialogItems,
    onCriticalPathsChange,
    saveProgramCriticalPaths,
    updateLinkFromDialog,
    removeLinkFromDialog,
    removeByWbsItemUuids,
    fetchProgramCriticalPaths: fetchCriticalPaths,
    hasUnsavedChanges,
  }
}
